<template>
    <!-- One route for everyone, with the sections that apply to who you are: Your Alerts (Admins), Your Team (anyone whose
         employee record has people reporting to it, on either account type) and the company settings (Admins). -->
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Settings</div>
                <div class="page-bar-main">Notifications</div>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <settings-layout v-if="loaded" desc="Choose what's sent and who it goes to.">
                <div class="form-wrapper settings-form">
                    <div v-if="!isAdminAccount && !showTeam && !showSignOffs && !showCompany" class="settings-card">There aren't any notifications for you to set up yet.</div>
                    <template v-if="isAdminAccount">
                    <!-- The signed-in admin's own alerts: API users.ts /me/notifications, adminNotifications.ts -->
                    <h2 class="settings-section-h">Your Alerts</h2>
                    <section class="settings-card">
                        <table v-if="alerts.length" class="ntf-table">
                            <thead><tr><th>Notify me when…</th><th>Email</th><th>Text</th></tr></thead>
                            <tbody>
                                <tr v-for="alert in alerts" :key="alert.key">
                                    <td>
                                        {{ alert.label }}<span v-if="alert.hint" class="item-hint ntf-alert-hint">{{ alert.hint }}</span>
                                        <!-- Only for some locations (accessRequest); none stored = every location -->
                                        <div v-if="alert.byLocation && isOn(alert) && $store.state.user.locations?.length > 1" class="chip-editor-item-opts thl-locs ntf-locs">
                                            <label v-for="loc in $store.state.user.locations" :key="loc" class="checkbox-input">
                                                <input type="checkbox" :checked="hasLocation(alert, loc)" @change="toggleLocation(alert, loc)" />
                                                <span>{{ loc }}</span>
                                            </label>
                                        </div>
                                    </td>
                                    <td v-for="via in ['email', 'text']" :key="via">
                                        <label :class="['item-private', 'ntf-switch', { 'ntf-locked': locked(alert, via) }]" :title="locked(alert, via) ? 'At least one of email or text stays on for this one' : (via == 'email' ? 'Email' : 'Text') + ': ' + alert.label">
                                            <input type="checkbox" v-model="mine.notifications[alert.key][via]" :disabled="locked(alert, via)" />
                                            <span class="item-private-track"><span class="item-private-knob"></span></span>
                                        </label>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <h3>Phone for Text Alerts</h3>
                        <phone-input v-model="mine.phone" />
                        <div class="chip-editor-hint ntf-phone-hint">
                            Your text alerts go to this number<span v-if="mine.email"> and email alerts to {{ mine.email }}</span>.
                            <span v-if="textsOn && !mine.phone" class="ntf-warn">Add a phone number to get your text alerts.</span>
                        </div>
                    </section>
                    </template>

                    <!-- A standard user's own alerts, on their account: API publicusers.ts /my-notifications,
                         employeeNotifications.ts PUBLIC_USER_ALERTS -->
                    <template v-if="showSignOffs">
                        <h2 class="settings-section-h">Your Alerts</h2>
                        <section class="settings-card">
                            <table class="ntf-table">
                                <thead><tr><th>Notify me when…</th><th>Email</th><th>Text</th></tr></thead>
                                <tbody>
                                    <tr v-for="alert in signOffAlerts" :key="alert.key">
                                        <td>{{ alert.label }}<span v-if="alert.hint" class="item-hint ntf-alert-hint">{{ alert.hint }}</span></td>
                                        <td v-for="via in ['email', 'text']" :key="via">
                                            <label :class="['item-private', 'ntf-switch', { 'ntf-locked': signOffLocked(alert, via) }]" :title="signOffLockReason(alert, via) || (via == 'email' ? 'Email' : 'Text') + ': ' + alert.label">
                                                <input type="checkbox" v-model="signOffs.notifications[alert.key][via]" :disabled="signOffLocked(alert, via)" />
                                                <span class="item-private-track"><span class="item-private-knob"></span></span>
                                            </label>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="chip-editor-hint ntf-phone-hint">
                                Texts go to your phone<template v-if="signOffs.phone"> ({{ formatPhone(signOffs.phone) }})</template><template v-if="signOffs.email"> and emails to {{ signOffs.email }}, from your employee record</template>.
                                <span v-if="!signOffs.email" class="ntf-warn">There's no email on your employee record, so you can only be texted, and people can't tag you in comments. Ask a Team Hub admin to add one.</span>
                                <span v-if="!signOffs.companyOn" class="ntf-warn">Sign-off reminders aren't turned on for your company yet.</span>
                            </div>
                        </section>
                    </template>

                    <!-- Alerts about the people who report to you, on your employee record: API employees.ts /my-notifications,
                         employeeNotifications.ts -->
                    <template v-if="showTeam">
                        <h2 class="settings-section-h">Your Team</h2>
                        <section class="settings-card">
                            <table class="ntf-table">
                                <thead><tr><th>Notify me when…</th><th>Email</th><th>Text</th></tr></thead>
                                <tbody>
                                    <tr v-for="alert in teamAlerts" :key="alert.key">
                                        <td>{{ alert.label }}<span v-if="alert.hint" class="item-hint ntf-alert-hint">{{ alert.hint }}</span></td>
                                        <td v-for="via in ['email', 'text']" :key="via">
                                            <label class="item-private ntf-switch" :title="(via == 'email' ? 'Email' : 'Text') + ': ' + alert.label">
                                                <input type="checkbox" v-model="team.notifications[alert.key][via]" />
                                                <span class="item-private-track"><span class="item-private-knob"></span></span>
                                            </label>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="chip-editor-hint ntf-phone-hint">
                                These go to the email<template v-if="team.email"> ({{ team.email }})</template> and phone<template v-if="team.phone"> ({{ formatPhone(team.phone) }})</template> on your employee record<template v-if="team.reports">, for the {{ team.reports }} {{ team.reports == 1 ? 'person' : 'people' }} who report to you</template>.
                                <span v-if="!team.reports" class="ntf-warn">No active employees have you as their Reporting To yet, so there's no one to summarize. Set Reporting To on their employee records.</span>
                                <span v-if="teamTextsOn && !team.phone" class="ntf-warn">Your employee record has no phone number, so texts can't be sent.</span>
                                <span v-if="!team.companyOn" class="ntf-warn">{{ isSuperAdmin ? 'Missed powerlist summaries are off for this company. Turn them on under Company Emails & Texts.' : "Missed powerlist summaries aren't turned on for your company yet." }}</span>
                            </div>
                        </section>
                    </template>

                    <!-- Company-wide settings (API companyconfig.ts POST /notifications checks the same roles):
                           Team Hub Employee Email Recipients: Team Hub Admins (any admin role) and superadmins
                           Sign-Off Reminders, Missed Powerlist Summaries: superadmins only (they start daily messages to
                           everyone); Admins see them read-only -->
                    <template v-if="showCompany">
                    <h2 class="settings-section-h">Company Emails &amp; Texts</h2>
                    <!-- Typed emails, since these can go to people outside the Ironbook (e.g. payroll) -->
                    <section v-if="canEditRecipients" class="settings-card">
                        <email-recipients-editor v-model="formData.EmployeeEmailsNew" label="Team Hub Employee Email Recipients" hint="Notified of employee updates and terminations for the locations ticked, when Send Admin Email is checked on the Edit Employee form. They don't need to be Ironbook users." :show-locations="true" />
                    </section>

                    <template v-if="isAdmin || isSuperAdmin">
                    <div v-if="!isSuperAdmin" class="chip-editor-hint ntf-company-locked">These company-wide reminders are managed by your Ironbook contact. Ask them if you'd like either one turned on or off.</div>
                    <!-- API cronjobs.ts dailySignReminderTexts; off by default (sql/company-sign-reminder-texts.sql) -->
                    <section :class="['settings-card', { 'ntf-readonly': !isSuperAdmin }]">
                        <label class="item-private ntf-toggle">
                            <span class="item-private-text">
                                <span class="item-private-title">Sign-Off Reminders</span>
                                <span class="item-hint">Remind standard users once a day when they have new or updated processes to sign. They're texted unless they choose email (sent to their employee record's email) or turn it off. Leave this off while your Ironbook is still being built out, so people aren't reminded about every change.</span>
                            </span>
                            <input type="checkbox" v-model="formData.SignReminderTexts" :disabled="!isSuperAdmin" />
                            <span class="item-private-track"><span class="item-private-knob"></span></span>
                        </label>
                    </section>

                    <!-- API employeeNotifications.ts dailyMissedPowerlists; off by default (sql/missed-powerlist-alerts.sql) -->
                    <section :class="['settings-card', { 'ntf-readonly': !isSuperAdmin }]">
                        <label class="item-private ntf-toggle">
                            <span class="item-private-text">
                                <span class="item-private-title">Missed Powerlist Summaries</span>
                                <span class="item-hint">Each morning, email managers which of the people who report to them didn't finish yesterday's powerlists. Managers can switch it to text or off for themselves. Set the days each powerlist runs in its editor, so days off aren't counted.</span>
                            </span>
                            <input type="checkbox" v-model="formData.MissedPowerlistAlerts" :disabled="!isSuperAdmin" />
                            <span class="item-private-track"><span class="item-private-knob"></span></span>
                        </label>
                    </section>
                    </template>
                    </template>

                    <div v-if="isAdminAccount || showTeam || showSignOffs || canEditRecipients" class="btn-row settings-save">
                        <button type="button" class="btn-lg" :disabled="disableButton" @click="submit()">Save Changes</button>
                        <button type="button" class="btn-lg btn-light" :disabled="disableButton" @click="load()">Cancel</button>
                    </div>
                </div>
            </settings-layout>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import EmailRecipientsEditor from '@/components/EmailRecipientsEditor.vue';
import SettingsLayout from '@/components/SettingsLayout.vue';
import PhoneInput from '@/components/PhoneInput.vue';
import { isValidPhone, normalizePhone } from '@/phone';
import { ADMIN_ALERTS } from '@/adminNotifications';
import { EMPLOYEE_ALERTS, PUBLIC_USER_ALERTS } from '@/employeeNotifications';

// An alert list's choices: the saved ones, else each alert's default
function simpleChoices(alerts: { key: string, email: boolean, text: boolean }[], saved: any): any {
    const choices: any = {};
    for(const alert of alerts) {
        choices[alert.key] = { email: alert.email, text: alert.text, ...(saved?.[alert.key] || {}) };
    }
    return choices;
}
const teamChoices = (saved: any) => simpleChoices(EMPLOYEE_ALERTS, saved);
const signOffChoices = (saved: any) => simpleChoices(PUBLIC_USER_ALERTS, saved);

function parseList(json: any): any[] {
    try {
        return JSON.parse(json) || [];
    } catch(e) {
        return [];
    }
}

// Every alert's email/text choice: the saved ones, else each alert's default (so the table always has a row to bind).
// The API fills in the defaults too; these only show until it answers.
function alertChoices(saved: any): any {
    const choices: any = {};
    for(const alert of ADMIN_ALERTS) {
        choices[alert.key] = { email: alert.email, text: alert.text, ...(saved?.[alert.key] || {}) };
        if(alert.required && !choices[alert.key].email && !choices[alert.key].text) choices[alert.key].email = true;
    }
    return choices;
}

export default defineComponent({
    name: 'NotificationsView',
    components: {
        EmailRecipientsEditor,
        SettingsLayout,
        PhoneInput
    },
    data() {
        return {
            formData: {
                SignReminderTexts: false,
                MissedPowerlistAlerts: false,
                EmployeeEmailsNew: [] as any[]
            },
            // The signed-in person's employee alerts (Your Team): reports = how many report to them
            team: {
                employeeID: null as number | null,
                reports: 0,
                companyOn: false,
                email: '',
                phone: '',
                notifications: teamChoices(null)
            },
            teamAlerts: EMPLOYEE_ALERTS,
            // A standard user's own alerts (Your Sign-Offs)
            signOffs: {
                phone: '',
                email: '',
                companyOn: false,
                notifications: signOffChoices(null)
            },
            signOffAlerts: PUBLIC_USER_ALERTS,
            loaded: false,
            // The signed-in admin's own settings
            mine: {
                phone: '',
                email: '',
                notifications: alertChoices(null)
            },
            alerts: ADMIN_ALERTS,
            // What was last loaded or saved, to tell whether there are unsaved changes (see snapshot())
            savedSnapshot: '',
            disableButton: false
        }
    },
    computed: {
        // Signed in with an admin account, any role (Editor, Content, ...): Your Alerts. Any admin user can own processes
        // and be tagged, and admin alerts go to every admin user (API adminNotifications.ts adminsForAlert)
        isAdminAccount(): boolean {
            return !!this.$store.state.user.isAdmin;
        },
        // The Admin role: sees the company reminder switches (read-only unless a superadmin)
        isAdmin(): boolean {
            return this.$store.state.user.role == 'Admin';
        },
        isSuperAdmin(): boolean {
            return !!this.$store.state.user.superAdmin;
        },
        // Team Hub Employee Email Recipients: Team Hub Admins, whatever their admin role (employees are the Team Hub's)
        canEditRecipients(): boolean {
            return this.isTeamHubAdmin || this.isSuperAdmin;
        },
        // Company Emails & Texts: the recipients editor (above), and the reminder switches (Admins read-only)
        showCompany(): boolean {
            return this.canEditRecipients || this.isAdmin;
        },
        isTeamHubAdmin(): boolean {
            return this.$store.state.user.teamHubRole == 'Admin';
        },
        textsOn(): boolean {
            return this.alerts.some(alert => this.mine.notifications[alert.key]?.text);
        },
        // Anyone with people reporting to them, or marked Is Manager on their employee record (so a manager with no one
        // assigned yet sees why there's nothing to summarize, instead of no section at all)
        showTeam(): boolean {
            return this.team.reports > 0 || (!!this.team.employeeID && !!this.$store.state.user.teamHubEmployee?.IsManager);
        },
        // Standard user sessions (admins sign in as admins, so this is never alongside Your Alerts)
        showSignOffs(): boolean {
            return !this.$store.state.user.isAdmin;
        },
        teamTextsOn(): boolean {
            return this.teamAlerts.some(alert => this.team.notifications[alert.key]?.text);
        },
        dirty(): boolean {
            return !!this.savedSnapshot && this.snapshot() != this.savedSnapshot;
        }
    },
    async created() {
        await this.load();
        this.loaded = true;

        this.$store.state.routeLoaded = true;
    },
    mounted() {
        window.addEventListener('beforeunload', this.warnUnload);
    },
    beforeUnmount() {
        window.removeEventListener('beforeunload', this.warnUnload);
    },
    beforeRouteLeave(to: any, from: any, next: any) {
        if(this.dirty && !window.confirm('You have unsaved changes. Leave without saving?')) {
            next(false);
            return;
        }
        next();
    },
    methods: {
        // Everything Save Changes sends; the phone as it's stored, so the phone field reformatting it isn't a change
        snapshot(): string {
            return JSON.stringify({
                phone: normalizePhone(this.mine.phone) || this.mine.phone || '',
                notifications: this.mine.notifications,
                team: this.team.notifications,
                signOffs: this.signOffs.notifications,
                company: this.formData
            });
        },
        // A standard user's switch that can't change: Email with no employee record email (unless it's on, so it can be
        // turned off), or a required alert's last channel that's on
        signOffLocked(alert: any, via: string): boolean {
            return !!this.signOffLockReason(alert, via);
        },
        signOffLockReason(alert: any, via: string): string {
            const choice = this.signOffs.notifications[alert.key];
            const other = via == 'email' ? 'text' : 'email';
            if(alert.required && choice?.[via] && !choice[other]) return 'At least one of email or text stays on for this one';
            if(via == 'email' && !this.signOffs.email && !choice?.email) return 'There is no email on your employee record';
            return '';
        },
        warnUnload(e: any) {
            if(this.dirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        },
        // A required alert's last switch that's on can't be turned off
        locked(alert: any, via: string): boolean {
            const choice = this.mine.notifications[alert.key];
            return !!alert.required && !!choice?.[via] && !choice[via == 'email' ? 'text' : 'email'];
        },
        isOn(alert: any): boolean {
            const choice = this.mine.notifications[alert.key];
            return !!(choice?.email || choice?.text);
        },
        hasLocation(alert: any, loc: string): boolean {
            const locations = this.mine.notifications[alert.key]?.locations;
            return !locations || locations.includes(loc);
        },
        // Every location ticked is stored as no list at all, so locations added later are included too
        toggleLocation(alert: any, loc: string) {
            const choice = this.mine.notifications[alert.key];
            const all: string[] = this.$store.state.user.locations || [];
            const current: string[] = choice.locations || all;
            const next = current.includes(loc) ? current.filter(l => l != loc) : [ ...current, loc ];
            if(all.every(l => next.includes(l))) delete choice.locations;
            else choice.locations = next;
        },
        // Admins: their own alerts and the company settings. Everyone: their employee alerts (Your Team).
        async load() {
            const loads = this.isAdminAccount ? [ this.getMine(), this.getTeam() ] : [ this.getTeam() ];
            if(this.showCompany) loads.push(this.getConfiguration());
            if(this.showSignOffs) loads.push(this.getSignOffs());
            await Promise.all(loads);
            this.savedSnapshot = this.snapshot();
        },
        async getSignOffs() {
            await axios.get(`${this.$store.state.apiUrl}/api/publicusers/my-notifications`)
                .then(resp => {
                    const data = resp.data || {};
                    this.signOffs = {
                        phone: data.phone || '',
                        email: data.email || '',
                        companyOn: !!data.companyOn,
                        notifications: signOffChoices(data.notifications)
                    };
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async getTeam() {
            await axios.get(`${this.$store.state.apiUrl}/api/employees/my-notifications`)
                .then(resp => {
                    const data = resp.data || {};
                    this.team = {
                        employeeID: data.employeeID || null,
                        reports: data.reports || 0,
                        companyOn: !!data.companyOn,
                        email: data.email || '',
                        phone: data.phone || '',
                        notifications: teamChoices(data.notifications)
                    };
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async getMine() {
            await axios.get(`${this.$store.state.apiUrl}/api/users/me/notifications`)
                .then(resp => {
                    this.mine = { phone: resp.data.phone || '', email: resp.data.email || '', notifications: alertChoices(resp.data.notifications) };
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async getConfiguration() {
            await axios.get(`${this.$store.state.apiUrl}/api/companyconfig`)
                .then(resp => {
                    const company = resp.data;
                    this.formData = {
                        SignReminderTexts: !!company.SignReminderTexts,
                        MissedPowerlistAlerts: !!company.MissedPowerlistAlerts,
                        EmployeeEmailsNew: parseList(company.EmployeeEmailsJSON)
                    };
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async submit() {
            if(this.isAdminAccount) {
                if(this.mine.phone && !isValidPhone(this.mine.phone)) {
                    alert('Enter a complete phone number for your text alerts, or leave it empty');
                    return;
                }
                if(this.textsOn && !this.mine.phone) {
                    alert('Add a phone number to get your text alerts, or turn them off');
                    return;
                }
                const limited = this.alerts.find(a => a.byLocation && this.isOn(a) && this.mine.notifications[a.key].locations?.length === 0);
                if(limited) {
                    alert(`Pick at least one location for "${limited.label}", or turn it off`);
                    return;
                }
            }
            if(this.showTeam && this.teamTextsOn && !this.team.phone) {
                alert('Your employee record has no phone number for team texts. Turn them off, or ask a Team Hub admin to add your phone.');
                return;
            }

            this.disableButton = true;

            const saves: Promise<any>[] = [];
            if(this.isAdminAccount) {
                saves.push(axios.post(`${this.$store.state.apiUrl}/api/users/me/notifications`, {
                    phone: this.mine.phone,
                    notifications: this.mine.notifications
                }));
            }
            // The API only applies the parts this person may change (recipients: Team Hub Admins; switches: superadmins)
            if(this.canEditRecipients || this.isSuperAdmin) {
                saves.push(axios.post(`${this.$store.state.apiUrl}/api/companyconfig/notifications`, {
                    EmployeeEmailsJSON: JSON.stringify(this.formData.EmployeeEmailsNew),
                    SignReminderTexts: this.formData.SignReminderTexts,
                    MissedPowerlistAlerts: this.formData.MissedPowerlistAlerts
                }));
            }
            if(this.showTeam) {
                saves.push(axios.post(`${this.$store.state.apiUrl}/api/employees/my-notifications`, { notifications: this.team.notifications }));
            }
            if(this.showSignOffs) {
                saves.push(axios.post(`${this.$store.state.apiUrl}/api/publicusers/my-notifications`, { notifications: this.signOffs.notifications }));
            }
            await Promise.all(saves)
                .then(() => {
                    this.savedSnapshot = this.snapshot();
                    // The company switch shows in Your Team's note straight away
                    if(this.isSuperAdmin) this.team.companyOn = this.formData.MissedPowerlistAlerts;
                    alert('Notifications have been updated!');
                })
                .catch(e => {
                    alert(typeof e?.response?.data == 'string' && e.response.data ? e.response.data : 'There was an error');
                    console.log(e);
                })

            this.disableButton = false;
        }
    }
});
</script>
